import { useState, useEffect } from 'react';

export default function Hero() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitUrl, setSubmitUrl] = useState('');

  useEffect(() => {
    const fetchFormUrl = async () => {
      try {
        const response = await fetch('https://readdy.ai/api/public/form/submit-addr?formId=quote-form');
        const data = await response.json();
        if (data.submitAddr) {
          setSubmitUrl(data.submitAddr);
        }
      } catch (error) {
        console.error('Failed to fetch form URL:', error);
      }
    };
    fetchFormUrl();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!submitUrl || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({
          company: '',
          name: '',
          phone: '',
          email: '',
          service: '',
          message: ''
        });
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-700"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="text-white pt-12">
            <p className="text-cyan-300 text-sm font-semibold mb-4 tracking-wider">스마트 물류의 새로운 기준</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              당신의 물류 파트너<br />
              <span className="text-cyan-400">로지뷰</span>
            </h1>
            <p className="text-xl text-blue-100 mb-12">
              최첨단 물류 시스템과 전문 인력으로<br />
              고객의 물류 혁신을 이끌어갑니다
            </p>

            <div className="flex gap-8 mb-12">
              <div>
                <div className="text-4xl font-bold text-cyan-400 mb-2">15+</div>
                <div className="text-blue-200">년 경험</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-cyan-400 mb-2">500+</div>
                <div className="text-blue-200">고객사</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-cyan-400 mb-2">99%</div>
                <div className="text-blue-200">정확도</div>
              </div>
            </div>
          </div>

          <div id="quote-form-section" className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-clipboard-check text-blue-600 text-xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">무료 견적 문의</h3>
              <p className="text-gray-600">최적의 물류 솔루션을 제안해 드립니다</p>
            </div>

            <form id="quote-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                  회사명 *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  placeholder="회사명을 입력하세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  담당자명 *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="이름을 입력하세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  연락처 *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="010-0000-0000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  이메일 *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="example@company.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">
                  서비스 종류 *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">선택하세요</option>
                  <option value="storage">보관/입출고</option>
                  <option value="distribution">유통가공</option>
                  <option value="delivery">배송대행</option>
                  <option value="fulfillment">풀필먼트</option>
                  <option value="custom">맞춤형 솔루션</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  문의내용
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  placeholder="자세한 문의사항을 입력해주세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                <i className="fas fa-paper-plane"></i>
                {isSubmitting ? '전송 중...' : '견적 문의하기'}
              </button>
            </form>

            {showSuccess && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-center">
                <i className="fas fa-check-circle mr-2"></i>
                문의가 접수되었습니다!
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}